import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { AnswerForType, FormResponseValueAnswer } from "@tellescope/types-models";
import { FORM_FIELD_VALIDATORS_BY_TYPE } from "@tellescope/validation";
import { i18n } from "@tellescope/i18n";
import {
  convertFormFieldType,
  getRequiredAddressKeys,
  isBookingBlockedByEligibility,
  isValueEmpty,
  resolveFormTranslation,
  type FormTranslationMap,
} from "../../utils";
import type { NextFieldLogicOptions } from "../../utils/types";
import { validateFieldByType } from "../validate-field-by-type";
import { translateValidatorError } from "../translate-validator-error";
import { getValueForValidation } from "./get-value-for-validation";
import { isRepeatMismatch } from "./resolve-repeat-mismatch";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";

export interface ValidateFieldValueArgs {
  field: TellescopeModelFromName<"form_fields">;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">> | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
  pendingFiles: Record<string, File[]>;
  repeatInputs: Record<string, string>;
  logicOptions: NextFieldLogicOptions;
  isSubFieldHidden: (fieldId: string) => boolean;
  /** Validated in place of the stored answer, for an input that has not been committed yet. */
  valueOverride?: FormResponseValueAnswer["value"];
  /**
   * The form's configured translations, so a message naming the field uses the title the enduser is
   * actually reading rather than the authored English.
   */
  translations?: FormTranslationMap;
}

const errorMessageOf = (err: unknown): string =>
  typeof err === "object" && err !== null && "message" in err ? String(err.message) : String(err);

const hasRequiredAddressKeys = (
  field: TellescopeModelFromName<"form_fields">,
  value: FormResponseValueAnswer["value"]
): boolean => {
  if (typeof value !== "object" || value === null) return false;
  return getRequiredAddressKeys(field.options).every((key) => {
    const entry = Reflect.get(value, key);
    return typeof entry === "string" && entry.trim().length > 0;
  });
};

/**
 * The one validator for a field's current answer, shared by the wizard's Next gate, the blocking-error
 * list, the inline error under an input, and the single-page form's completeness check.
 *
 * Synchronous and pure: the result is a function of the answers as rendered, so callers derive it
 * on every render rather than storing it. Returns the message to show, or null when the field
 * passes. Fields the enduser cannot act on (display-only, hidden, eligibility, locked, blocked by a
 * failed eligibility check) always pass so they can never strand the wizard.
 *
 * A Question Group validates each visible sub-field and returns the first error, so a group is
 * gated and reported as one page.
 */
export const validateFieldValue = (args: ValidateFieldValueArgs): string | null => {
  const { field, fieldsMap, currentResponses, pendingFiles, repeatInputs, isSubFieldHidden, translations } = args;

  const type = convertFormFieldType(field.type);
  if (type === null) return null;
  if (isSubFieldHidden(field.id)) return null;

  if (type === "Question Group") {
    return (field.options?.subFields ?? []).reduce<string | null>((firstError, sf) => {
      if (firstError) return firstError;
      const subField = fieldsMap?.[sf.id];
      if (!subField) return null;
      return validateFieldValue({ ...args, field: subField, valueOverride: undefined });
    }, null);
  }

  if (field.type === "Bridge Eligibility" || field.type === "Candid Eligibility" || field.type === "Stedi Eligibility")
    return null;
  if (field.type === "Hidden Value") return null;
  if (field.type === "Redirect") return null;

  const response = currentResponses[field.id];
  if (response?.disabled) return null;
  if (isBookingBlockedByEligibility(field, Object.values(currentResponses))) return null;

  const validator = FORM_FIELD_VALIDATORS_BY_TYPE[field.type];
  if (!validator) return null;

  const rawValue = args.valueOverride !== undefined ? args.valueOverride : response?.answer.value;
  const value =
    (field.type === "string" || field.type === "stringLong") && typeof rawValue === "string"
      ? rawValue.trim()
      : rawValue;
  const pendingCount = pendingFiles[field.id]?.length ?? 0;
  const isEmpty = isValueEmpty(value) && pendingCount === 0;
  // The title names the question on screen, so it must match what the enduser is reading.
  const requiredMessage = i18n.t("{{title}} is required", {
    ns: "forms",
    title: resolveFormTranslation(field.title, translations),
  });

  if (isRepeatMismatch({ field, value, confirmation: repeatInputs[field.id] ?? "" }))
    return i18n.t("Values do not match", { ns: "forms" });

  // Counted here rather than in `validateFieldByType`, which sees only the stored value: files chosen
  // but not yet uploaded satisfy the field until submit uploads them.
  if (field.type === "file" || field.type === "files") {
    const storedCount = Array.isArray(value) ? value.length : isValueEmpty(value) ? 0 : 1;
    const totalCount = storedCount + pendingCount;
    if (totalCount === 0) return field.isOptional ? null : requiredMessage;
    const { min, max } = field.options ?? {};
    if (min !== undefined && totalCount < min)
      return i18n.t("At least {{min}} file(s) are required", { ns: "forms", min });
    if (max !== undefined && totalCount > max)
      return i18n.t("At most {{max}} file(s) are allowed", { ns: "forms", max });
    if (storedCount === 0) return null;
    try {
      validator(getValueForValidation<keyof AnswerForType>(type, value), field.options, field.isOptional);
      return null;
    } catch (err) {
      return translateValidatorError(errorMessageOf(err));
    }
  }

  try {
    if (field.type === "multiple_choice" || field.type === "Dropdown") {
      const selectedOptions = Array.isArray(value) ? value : [];
      if (field.type === "multiple_choice" && field.options?.radio && selectedOptions.length > 1) {
        return i18n.t("Only 1 choice is allowed", { ns: "forms" });
      }
    } else if (field.type === "signature") {
      const signatureError = validateFieldByType(field, value, translations);
      if (signatureError) return signatureError;
    } else {
      try {
        validator(getValueForValidation<keyof AnswerForType>(type, value), field.options, field.isOptional);
      } catch (err) {
        if (!(isEmpty && field.isOptional)) return translateValidatorError(errorMessageOf(err));
      }
    }

    const additionalError = validateFieldByType(field, value, translations);
    if (additionalError) return additionalError;

    if (isEmpty) return field.isOptional ? null : requiredMessage;

    if (field.type === "Address" && !hasRequiredAddressKeys(field, value)) {
      return i18n.t("Please complete every required address field", { ns: "forms" });
    }

    return null;
  } catch (error) {
    return translateValidatorError(errorMessageOf(error));
  }
};
